import Backbone from 'backbone'
import template from './upload-form.hbs'
import globals from 'scoreboard/components/globals'
import fileInfo from 'scoreboard/components/files/file-info'
import i18n from 'scoreboard/components/i18n'
import _ from 'lodash'
import FileUploader from 'scoreboard/components/files/file-uploader'
import FormView from 'scoreboard/components/form-view'
import appDisplay from 'scoreboard/components/app-display'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import $ from 'jquery'
import FileDrop from 'scoreboard/components/files/file-drop'
import {getIcon} from 'scoreboard/components/files/file'
import NewRecordOptions from 'scoreboard/datasets/new-record-options'
import {
	handleMissingColumnsException, hasColumnsToRemapTo
} from 'scoreboard/imports/new-column-name-chooser-wizard'
import {filenameIsSpreadsheet} from '../spreadsheet-file-utils'

var UploadForm = Backbone.View.extend({

	tagName: 'form',

	className: 'upload-form',

	events: {
		'click .cancel': 'close',
		'click .new-record-options-container li': 'onClickNewRecordOption'
	},

	/**
	 * Form for uploading documents and revisions.  There are a few ways this is
	 * accessed: when uploading revisions, when creating a document, when
	 * creating the first document in an organization, and when uploading an
	 * image to the shared image library through dashboards.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {File} options.model the file (formerly known as a "managed
	 * document"
	 *
	 * @param {Object} [options.files] a list of uploaded files (possibly
	 * dropped into a drag and drop area)
	 *
	 * @param {Boolean} [options.maintainSelection=false] indicates the
	 * selection in the nav pane should remain unchanged after the upload
	 *
	 * @param {Gardener} [options.gardener] the gardener used to determine the
	 * document's addPosition based on its relativeId (see the submit method)
	 *
	 * @param {Array} [options.accept] restrictions on the types of
	 * files that can be uploaded.  e.g. '.csv, text/csv'.  Note the extension
	 * and mime type must both be specified because some browsers want to see
	 * the extension and others want to see the mime type
	 * (see https://stackoverflow.com/a/23706177/393274)
	 *
	 * @param {String} [options.message] message to display to the user before
	 * selecting or auto-uploading a file
	 *
	 * @param {String} [options.extensionMessage] custom message to display if
	 * file with invalid extension was uploaded
	 *
	 * @param {Boolean} [options.revisionDrop] indicates if file was dropped
	 * as a revision to an existing file
	 *
	 * @param {Boolean} [options.folderDrop] indicates if file was dropped
	 * onto a folder
	 */
	initialize: function(options) {
		this.options = options || {}

		var UploadFormView = FormView.extend({
			test: () => this.test()
		})

		this.formView = new UploadFormView({
			el: this.el,
			submit: this.submit.bind(this)
		})
		this.formView.on('change', () => {
			if (this.uploadInProgress) {
				// #36779 Prevent the button from enabling while a file is uploading
				this.formView.primaryButton.disable()
			}
		})

		// Since this upload form needs to manipulate the uploader directly depending on what's
		// going on in the form, create a custom file uploader here in order to have greater control.
		this.fileUploader = new FileUploader({
			url: 'api/files/revision',
			dataFn: this.uploadData.bind(this),
			files: this.options.files,
			accept: this.options.accept
		})

		let dropMessage = this.model.isNew() ? 'documents.upload.drop.document.1' :
		'documents.upload.drop.revision.1'
		this.fileDrop = new FileDrop({
			fileUploader: this.fileUploader,
			noDropMessage: window.FormData ? null : i18n.get('documents.upload.noDrop'),
			dropLabel: i18n.get(dropMessage),
			dropSubLabel: i18n.get('documents.upload.drop.document.2'),
			revisionDrop: this.options.revisionDrop
		})

		this.fileUploader.on('done', this.uploadFinished.bind(this))
			.on('fileChosen', this.fileChosen.bind(this))
			.on('error', this.uploadError.bind(this))
			.on('start', this.uploadStart.bind(this))
			.on('extensionError', (message)=> {
				this.extensionError(message)
			})

		// If a file has already been selected and there's a message to display
		// to the user, have them confirm before uploading the file
		this.confirmBeforeUpload = !!this.options.message && !_.isEmpty(this.options.files)
	},

	// called when a file is selected, but not uploaded
	fileChosen: function() {
		this.formView.change()
		this.showNewRecordOptions()
	},

	// displays the new record options, if applicable
	// returns true if the options are displayed
	showNewRecordOptions() {
		let isSpreadsheet = this._isSpreadsheet(this.fileUploader.getFile())
		this.$el.toggleClass('two-box-options-dialog', isSpreadsheet)
		return isSpreadsheet
	},

	test: function() {
		let errMsg = FormView.prototype.test.apply(this, arguments)

		if (errMsg) {
			return errMsg
		}

		if (!_.isNil(this.missingColumnsException)) {
			return this.getMissingDatasetColumnsException()
		}

		if (_.isNil(this.fileUploader.files)) {
			return i18n.get('common.error.mustSelect.file')
		}
	},

	render: function() {
		this.$el.append(template(this.context()))

		this.options.wizard && this.options.wizard.render({
			formView: this.formView
		})

		this.$('.file-drop').replaceWith(this.fileDrop.el)
		this.fileDrop.render()

		// #34282 don't display new record options for new file
		// #34451 only display new record options if file is used by datasets
		if (!this.model.isNew() && this.hasDatasets()) {
			this.newRecordOptions = new NewRecordOptions({
				noConfirmation: true
			})
			this.$('.new-record-options-container').html(this.newRecordOptions.el)
			this.newRecordOptions.render()
		}

		this.labelField = new MarkedField({
			el: this.$('[name=label]'),
			validator: requiredValidator
		}).render()

		this.formView.start()

		let hasFile = !this.fileUploader.isFileUnspecified()
		this.$el.toggleClass('is-file-drop-visible', !hasFile)

		if (hasFile && this._isSpreadsheet(this.fileUploader.getFile())) {
			this.$el.addClass('two-box-options-dialog')
		}

		// #29733 if this was a revision dropped via the file-drop-overlay
		// #56151 or if this was a file dropped to a folder
		// and we don't have a file, this indicates there was a validation
		// error (file did not have a valid file extension)
		let isDrop = this.options.revisionDrop || this.options.folderDrop
		if (isDrop && !hasFile) {
			const errorMessage = this.options.extensionMessage
			if (errorMessage) {
				// if we have an extension error message, display it
				this.extensionError(errorMessage)
			} else {
				// validate to display the default extension error message
				this.fileDrop.fileUploader.validateExtension()
			}
		}

		// TODO: https://github.com/SpiderStrategies/Scoreboard/issues/28617
		// this.fileUploader.$el.after(new BoxOptions({ availableOptions: [{
		// 	title: i18n.get('documents.upload.doNothing'),
		// 	icon: 'layout-blank',
		// 	value: 'nothing'
		// },{
		// 	title: i18n.get('documents.upload.makeDataset'),
		// 	icon: 'add-widget-dataset',
		// 	value: 'new-dataset'
		// },{
		// 	title: i18n.get('documents.upload.updateMetric'),
		// 	icon: 'metric-large',
		// 	value: 'update-metric'
		// },{
		// 	title: i18n.get('documents.upload.updateInitiative'),
		// 	icon: 'initiative-large',
		// 	value: 'update-initiative'
		// }]}).render().el)

		return this
	},

	onClickNewRecordOption: function(e) {
		this.newRecordOptions.handleOptionSelected(e, replaceAllRecords =>
			this.replaceAllRecords = replaceAllRecords)
	},

	context: function() {
		var context = {
			message: this.options.message,
			primaryButtonKey: 'common.save'
		}
		if (this.model.isNew()) {
			context.title = i18n.get('documents.upload.document')
			context.showName = true
		} else {
			context.title = i18n.get('documents.upload.revision')
			context.showName = false
		}

		context.dropLabel = i18n.get(this.options.instructions + '.1'),
		context.dropSubLabel = i18n.get(this.options.instructions + '.2')

		return context
	},

	// callback executed before the upload starts
	uploadStart: function() {
		this.uploadInProgress = true
		_.result(this.formView, 'primaryButton.disable')
		_.result(this.labelField, 'clearError')
		_.result(this.formView, 'clearErrors')
	},

	// callback creating the data to be submitted to the server
	uploadData: function(files) {
		this.files = files
		var fileName = files[0].name

		var data = {
			fileName: fileName,
			replaceAllRecords: _.isNil(this.replaceAllRecords) ? false :
				this.replaceAllRecords
		}

		// if creating the first revision
		if (this.model.isNew()) {
			data.organizationId = globals.orgId

			// give the document a name, if none was specified
			var label = this.$('[name=label]')
			if (_.isEmpty(label.val())) {
				_.invoke(this.labelField, 'clearError')
				label.val(fileInfo.fileName(fileName))
			}
		// if creating a subsequent revision
		} else {
			data.documentId = this.model.id
		}

		this.revision = data
		return data
	},

	// callback for when the upload finished.  data is any data returned by
	// the server
	uploadFinished: function(data) {
		// #36954 if we need the user to choose new column names, we need to simply
		// move to the next step
		if (this.moveToNextStep && this.options.wizard && !this.options.wizard.isLastStep()) {
			this.moveToNextStep = false
			this.options.wizard.next(this.options)
		// #36954 if #uploadFinished is called with no data param, don't
		// attempt to continue
		} else if (data) {
			this.missingColumnsException = null
			this.uploadInProgress = false
			this.$('.reset').addClass('is-not-displayed')
			this.$('.banner-description').removeClass('is-not-displayed')
			_.extend(this.revision, data, {
				size: this.files[0].size,
				lastModified: new Date(),
				label: this.revision.fileName
			})
			this.formView.change()
			this.uploaded = true
			this.$el.removeClass('is-submitting-form is-full-opacity')

			// #29617 automatically advance to next step if this is in a wizard
			if (this.options.wizard && !this.options.wizard.isLastStep()) {
				this.submit()
			} else if (this.hasDatasets() && !this.confirmed) {
				this.showNewRecordOptions()
			}
		}
	},

	hasDatasets: function() {
		return !_.isEmpty(_.get(this.model.attributes, 'datasets', []))
	},

	// saves the managed file after it is uploaded
	_onSubmit: function() {
		if (this.confirmBeforeUpload && !this.uploaded) {
			// If we're in "confirm before upload" mode and the file hasn't been
			// uploaded yet, clicking on the button means it's time to upload the
			// file. Calling render() of the fileUploader will accomplish this
			this.$('.button.done').text(i18n.get('common.done'))
			this.fileUploader.render()
			return
		}

		// this breaks encapsulation, but IDK what else to do.  Realistically to
		// determine the position within the tree you have to have reference to
		// the underlying kalpa-tree.  I could try to factor the position code
		// out of the gardener, but I don't really see the point because then
		// we'd just have a dependency on the tree rather than the gardener.
		var gardener = this.options.gardener || appDisplay.navView().gardener
		var relativeId = this.model.get('relativeId')
		var relativeNode = relativeId && gardener.tree.get(relativeId)

		// #26314 - If there is no relative node we might have a different tree
		// (because we're spectating a folder), so just use the folder id as the parent
		let position = {parentId: relativeId}
		if (relativeNode) {
			position = gardener.getAddPosition(this.model, relativeId)
		}

		if (this.model.isNew()) {
			this.model.save(_.extend({
				revisionId: this.revision.id,
				label: this.$('[name=label]').val()
			}, position), {
				error: function(model, xhr) {
					// #31389 Reset the form so it doesn't look like it is stuck
					this.formView.error(xhr)
					this.formView.primaryButton.enable()
				}.bind(this),
				success: function() {
					this.model.set('revisions', [ this.revision ])
					// #20152 the server sends back the height/width with the
					// revision instead of the main document for efficiency
					// reasons so copy over the required data here or else we
					// can't display the image size correctly in the image
					// library
					this.model.set('height', this.revision.height)
					this.model.set('width', this.revision.width)
					// Set the correct file icon if the model is of type file
					let icon = getIcon(this.model.get('type'),
						this.revision.fileName, true)
					this.model.set('icon', icon)
					// important: make sure add:domain-object goes before
					// this.trigger('done') so that all trees are updated
					// before we say that everything is done.  required for
					// documents-nav.js:addDocument so that it can select the
					// correct item in the tree
					Backbone.trigger('add:domain-object', this.model, relativeId,
						{ maintainSelection: this.options.maintainSelection })
					this.trigger('done', this.model)
				}.bind(this)
			})
		} else {
			const params = $.param({ replaceAllRecords: this.replaceAllRecords })
			$.post(`api/files/${this.model.id}/revisions/${this.revision.id}/confirm?${params}`)
				.then(() => {
					this.model.revisions.add(this.revision)
					// #29617 advance to next step if this is in a wizard and
					// there is another step
					if (this.options.wizard) {
						if (!this.options.wizard.isLastStep()) {
							this.options.wizard.next()
						} else {
							this.options.wizard.finish(this.model)
							this.trigger('done', this.model)
						}
					} else {
						this.trigger('done', this.model)
					}
				})
		}
	},

	// executed when user clicks on done
	submit: function(e) {
		if (this.hasDatasets()) {
			// #35717 let the user decide to append or overwrite data on linked datasets
			this.newRecordOptions.confirmReplaceIfNeeded((replaceAllRecords, cancelled) => {
				if (cancelled) {
					this.formView.start()
					this.formView.change()
					return
				}
				this.replaceAllRecords = replaceAllRecords
				this.confirmed = true
				if (this.uploaded) {
					// #36954 when a file is dragged on the document screen,
					// the file is already uploaded, so just submit
					this._onSubmit()
				} else {
					// #36954 The revision upload dialog was opened and the user
					// pressed the save button; upload the file
					this.moveToNextStep = true
					this.fileUploader.upload()
				}
			})
		} else {
			this._onSubmit()
		}
	},

	// #21263 handles closing the dialog by either the cancel button or esc key
	// delete anything that was already uploaded
	close: function () {
		// even if you try to call .abort on the XHR it doesn't really abort
		// and the file is created anyway.  So it will be better to just let
		// the upload finish and then delete the revision
		if (this.fileUploader.xhr) {
			this.fileUploader.xhr.then(function(data) {
				data.id && this.model.destroyRevision(data.id)
			}.bind(this))
		} else {
			var revisionId = _.get(this, 'revision.id')
			if (revisionId) {
				revisionId && this.model.destroyRevision(revisionId)
			}
		}
		this.trigger('cancel')
	},

	uploadError: function(xhr) {
		this.uploadInProgress = false
		if (this.replaceAllRecords || this.replaceAllRecords === false) {
			this.options.params = _.extend(this.options.params || {},
				{ replaceAllRecords: this.replaceAllRecords })
		}
		if (handleMissingColumnsException(xhr, this.options, false)) {
			const hasColumnsToRemap = hasColumnsToRemapTo(xhr)
			// Finish the upload process so the user can continue to remapping the columns
			if (hasColumnsToRemap) {
				this.showNewRecordOptions()
				this.fileUploader.uploadFinished()
				this.formView.change()
				this.$el.removeClass('is-submitting-form is-full-opacity')
				// #60043 if this is a revision dropped on documents-overview,
				// we don't want to automatically move to the next step of the
				// wizard.  We want to allow the user to select add or replace
				// records first
				if (!this.options.revisionDrop) {
					this.options.wizard.next()
				}
			} else {
				const ex = xhr?.responseJSON
				this.missingColumnsException = ex
				this.formView.errorMessage(
					this.getMissingDatasetColumnsException(), ex.stackTrace)
			}
			this.options.successHandler = () => {
				// Make sure the new file shows up in the file revision list
				// when everything is saved
				_.extend(this.revision, {
					lastModified: new Date(),
					label: this.revision.fileName
				})
				this.model.revisions.add(this.revision)
				this.options.wizard.finish(this.model)
				this.missingColumnsException = null
				this.trigger('done', this.model)
			}
		} else {
			this.fileUploader.reset()
			if (xhr.statusText !== 'abort') {
				this.formView.error(xhr)
			}
			let data = xhr.responseJSON
			let type = _.get(data, 'object.type')
			if ('metric-value' === type || 'initiative-status' === type ||
				'dataset' === type) {
				let url, key
				if (type === 'dataset') {
					url = `datasets/${data.object.id}`
					key = 'documents.revision.invalid.dataset'
				} else {
					url = `admin/imports/${data.object.id}`
					key = 'documents.revision.invalid.import'
				}
				let link = `<a href="${url}" class="sp-navigate">` +
					data.object.label + '</a>'
				let msg = i18n.get(key, link)
				this.$('.banner').prepend(`<div class="paragraph">${msg}</div>`)
			}
		}
	},


	getMissingDatasetColumnsException: function() {
		const ex = this.missingColumnsException
		return this.formView.multiCauseMessage(ex?.missing, {
			prelude: ex?.msg
		})
	},

	extensionError: function(message) {
		this.$('.banner-description').addClass('is-not-displayed')
		// #29733 display custom extension error message if provided
		if (!_.isNil(this.options.extensionMessage)) {
			message = this.options.extensionMessage
		}
		this.formView.errorMessage(message)
	},

	_isSpreadsheet(file) {
		return filenameIsSpreadsheet(file?.name)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.formView, this.fileDrop, this.labelField, this.newRecordOptions], 'remove')
	}

})

export default UploadForm

UploadForm.__filename__ = 'components/files/upload-form.js'
